import { inject, Injectable, NgZone } from '@angular/core';
import { MapGeocoder } from '@angular/google-maps';
import { Observable, catchError, from, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { GetCabinaIdByPointGQL, GetCabinaIdByPointQueryVariables } from '../_graphql/generated';

/** Props condivise dai formly field type che abilitano la ricerca cabina per indirizzo. */
export interface AddressSearchProps {
  /** Opt-in: mostra il toggle [Cabina/Città] [Indirizzo] nell'header del pannello. */
  addressSearch?: boolean;
  /** Campi con cui riconoscere la cabina risolta fra le options. Default: ['id', 'cod_ac']. */
  addressSearchMatchKeys?: string[];
}

export interface AddressSuggestion {
  placeId: string;
  descrizione: string;
}

export interface CabinaByAddress {
  id: number;
  cod_ac: string | null;
  elenco_citta: string | null;
}

const MIN_QUERY_LENGTH = 3;

/**
 * Autocomplete Places + geocoding + risoluzione della cabina la cui area
 * geografica contiene l'indirizzo scelto. Stateless (providedIn: 'root'):
 * la libreria Places e l'AutocompleteService sono condivisi fra tutti i
 * dropdown della pagina.
 */
@Injectable({ providedIn: 'root' })
export class AddressCabinaSearchService {
  // MapGeocoder carica da sé la libreria 'geocoding' on-demand e rientra
  // in zone: non serve un Geocoder istanziato a mano come in
  // _formly/_types/geocode.type.ts.
  private readonly _geocoder = inject(MapGeocoder);
  private readonly _ngZone = inject(NgZone);
  private readonly _cabinaByPoint = inject(GetCabinaIdByPointGQL);

  private _autocomplete?: google.maps.places.AutocompleteService;
  private _placesLoading?: Promise<void>;

  /** true se lo script Google Maps (bootstrap loader di index.html) è stato caricato. */
  hasMapsLoader(): boolean {
    return !!(globalThis as { google?: typeof google }).google?.maps?.importLibrary;
  }

  /** Suggerimenti Google Places per la query digitata (solo Italia). */
  suggest(query: string): Observable<AddressSuggestion[]> {
    const input = query.trim();
    if (input.length < MIN_QUERY_LENGTH) return of([]);

    return from(this._ensurePlaces()).pipe(
      switchMap(() => {
        if (!this._autocomplete) return of<AddressSuggestion[]>([]);
        return new Observable<AddressSuggestion[]>((observer) => {
          this._autocomplete!.getPlacePredictions(
            { input, componentRestrictions: { country: 'it' } },
            (predictions, status) => {
              // ⚠️ il callback di AutocompleteService gira fuori dalla zona Angular
              this._ngZone.run(() => {
                const ok = status === google.maps.places.PlacesServiceStatus.OK;
                observer.next(
                  ok && predictions
                    ? predictions
                        .filter((p): p is google.maps.places.AutocompletePrediction => !!p.place_id)
                        .map((p) => ({ placeId: p.place_id, descrizione: p.description ?? '' }))
                    : [],
                );
                observer.complete();
              });
            },
          );
        });
      }),
      catchError(() => of<AddressSuggestion[]>([])),
    );
  }

  /**
   * Indirizzo scelto → punto → cabina la cui area geografica lo contiene.
   * 🔥 energy_village_cabina non ha campi geografici: la geometria sta su
   * energy_village_area.geom, per questo si passa da GetCabinaIdByPoint.
   */
  resolveCabina(placeId: string): Observable<CabinaByAddress | null> {
    return this._geocoder.geocode({ placeId, language: 'it' }).pipe(
      switchMap(({ results, status }) => {
        const location = results?.[0]?.geometry?.location;
        if (status !== google.maps.GeocoderStatus.OK || !location) return of(null);

        const point: GetCabinaIdByPointQueryVariables['point'] = {
          type: 'Point',
          coordinates: [location.lng(), location.lat()],
        };

        return this._cabinaByPoint
          .fetch(
            { point },
            {
              // ⚠️ le aree cabina sono leggibili solo col ruolo anonymous, stesso
              // setup di staging/add/singolo/dati-utenza/dati-utenza.component.ts
              context: { headers: { 'x-hasura-role': 'anonymous' } },
              fetchPolicy: 'network-only',
            },
          )
          .pipe(
            // 🔥 se più aree contengono il punto si prende la prima: le aree
            // cabina non si sovrappongono, l'overlap è al più un bordo condiviso.
            map((res): CabinaByAddress | null => {
              const cabina = res?.data?.energy_village_area?.[0]?.cabina;
              if (!cabina) return null;
              return { id: cabina.id, cod_ac: cabina.cod_ac ?? null, elenco_citta: cabina.elenco_citta ?? null };
            }),
          );
      }),
      catchError(() => of(null)),
    );
  }

  private async _ensurePlaces(): Promise<void> {
    const maps = (globalThis as { google?: typeof google }).google?.maps;
    if (!maps) return;
    if (!maps.places) {
      this._placesLoading ??= maps.importLibrary('places').then(() => undefined);
      await this._placesLoading;
    }
    this._autocomplete ??= new google.maps.places.AutocompleteService();
  }
}
